<template>
    <div class="placeBet">
        <div class="placeBet-stake">
            <div
                ref="stakeRef"
                class="placeBet-stake-amount JARVIS_BeTticket_TextField_Stake"
                v-bind:class="{ _keyin: hasStake }"
                readonly="true"
                data-testid="betslip-stake-input"
                v-on:click="setIsShowStakePad(false)"
            >
                <span class="notranslate placeBet-stake-currency">{{ placeBetHint }}</span>
                <span v-bind:key="stakeValueWithZero" class="placeBet-stake-amount-number">{{ stakeValueWithZero }}</span>
                <span
                    v-if="hasStake"
                    class="placeBet-stake-clear JARVIS_BeTticket_Btn_ClearStake"
                    v-on:click="clearStake"
                >
                    <SvgIcon iconName="systemIcon_close" />
                </span>
            </div>
        </div>
        <button
            v-if="shouldShowDeposit && isQuickDepositLoaded"
            type="button"
            class="button deposit JARVIS_BetTicket_Btn_Deposit"
            v-on:click="clickDeposit"
        >
            {{ i18n.t('comm_deposit_button') }}
        </button>
        <button
            v-else
            type="button"
            class="button"
            v-bind:class="[{ _disable: isDisableBtn }, placeBetGa]"
            data-testid="betslip-place-bet"
            v-on:click="placeBet"
        >
            {{ i18n.t('placeBet_button_placeBet') }}
        </button>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, onMounted, onUnmounted, ref, toRefs, watch } from 'vue';
    import { PlayerType } from '@/interface/enum';
    import { isBalanceLessMinBet } from '@/libraries/betSlip/stake';
    import { useQuickDeposit } from '@/libraries/composables/useQuickDeposit';
    import { useStakeInput } from '@/libraries/composables/useStakeInput';
    import { i18n } from '@/libraries/i18n';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'PlaceBet',
        components: {
            SvgIcon,
        },
        emits: ['placeBet', 'changeStake'],
        props: {
            stake: {
                type: Number,
                default: 0,
            },
            isDisableBtn: {
                type: Boolean,
                default: false,
            },
            placeBetGa: {
                type: String,
                default: 'JARVIS_BetTicket_Btn_PlaceBet',
            },
            balance: {
                type: Number as PropType<number | null>,
                default: null,
            },
            minBet: {
                type: Number,
                default: 0,
            },
            price: {
                type: Number,
                default: 0,
            },
            isRedeemVoucher: {
                type: Boolean,
                default: false,
            },
        },
        setup(props, context) {
            const { setIsShowStakePad } = useBetSlipStore();
            const { stake, balance, minBet, price, isRedeemVoucher } = toRefs(props);

            const { isEuroSite, isShowDepositInTicketEnabled } = storeToRefs(useFeatureToggleStore());

            const { customerInfo } = storeToRefs(useAccountStore());

            const { currency, hasStake, stakeValueWithZero } = useStakeInput(stake);

            const placeBetHint = computed(() => (isEuroSite.value ? i18n.t('placeBet_stake_currency_EURO') : currency.value));

            const shouldShowDeposit = computed(() => {
                if (!isShowDepositInTicketEnabled.value) return false;
                if (isRedeemVoucher.value) return false;

                if (balance.value === null) return false;

                return [PlayerType.B2C, PlayerType.B2B2C].includes(customerInfo.value.playerType)
                    && (isBalanceLessMinBet(balance.value, minBet.value, price.value) || Math.floor(balance.value) <= 0);
            });

            function placeBet() {
                if (props.isDisableBtn) return;

                context.emit('placeBet');
            }

            const stakeRef = ref<HTMLDivElement>();
            function focusInput() {
                stakeRef.value?.focus();
            }

            function clearStake() {
                context.emit('changeStake', 0);
                focusInput();
            }

            const { isQuickDepositLoaded, clickDeposit, loadQuickDepositScript } = useQuickDeposit();

            watch([balance, shouldShowDeposit], async ([newBalance, newShouldShowDeposit]) => {
                if (newBalance !== null && newShouldShowDeposit) {
                    await loadQuickDepositScript();
                }
            }, { immediate: true });

            onMounted(() => {
                window.eventEmitter.on('focusInput', focusInput);
                focusInput();
            });

            onUnmounted(() => {
                window.eventEmitter.off('focusInput', focusInput);
            });

            return {
                i18n,
                setIsShowStakePad,
                placeBetHint,
                hasStake,
                stakeValueWithZero,
                clearStake,
                placeBet,
                clickDeposit,
                shouldShowDeposit,
                isQuickDepositLoaded,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/placeBet";
</style>
